/**
 * CascadeImpactDisplay
 *
 * Shows the cascade impact of a deactivation
 */

import React from 'react';
import {
  BuildingOffice2Icon,
  UsersIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

export interface CascadeImpact {
  enterprisesCount?: number;
  channelPartnersCount: number;
  organizationsCount: number;
  usersCount: number;
  encountersCount?: number;
}

interface CascadeImpactDisplayProps {
  impact: CascadeImpact;
  showWarning?: boolean;
}

export const CascadeImpactDisplay: React.FC<CascadeImpactDisplayProps> = ({
  impact,
  showWarning = true
}) => {
  const hasImpact = impact.channelPartnersCount > 0 ||
    impact.organizationsCount > 0 ||
    impact.usersCount > 0 ||
    impact.encountersCount > 0;

  return (
    <div className="space-y-3">
      {showWarning && hasImpact && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            This deletion will affect the following entities. All changes are permanent and cannot be undone.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {impact.channelPartnersCount > 0 && (
          <div className="flex items-center gap-2 p-3 bg-violet-50 rounded-lg">
            <UsersIcon className="h-5 w-5 text-violet-600" />
            <div>
              <p className="text-lg font-semibold text-violet-900">
                {impact.channelPartnersCount}
              </p>
              <p className="text-xs text-violet-600">Channel Partners</p>
            </div>
          </div>
        )}

        {impact.organizationsCount > 0 && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
            <BuildingOffice2Icon className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-lg font-semibold text-blue-900">
                {impact.organizationsCount}
              </p>
              <p className="text-xs text-blue-600">Organizations</p>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
          <UsersIcon className="h-5 w-5 text-gray-600" />
          <div>
            <p className="text-lg font-semibold text-gray-900">
              {impact.usersCount}
            </p>
            <p className="text-xs text-gray-600">Users Deactivated</p>
          </div>
        </div>

        {impact.encountersCount > 0 && (
          <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg">
            <DocumentTextIcon className="h-5 w-5 text-red-600" />
            <div>
              <p className="text-lg font-semibold text-red-900">
                {impact.encountersCount}
              </p>
              <p className="text-xs text-red-600">Encounters (PHI)</p>
            </div>
          </div>
        )}
      </div>

      {impact.encountersCount > 0 && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">
            <strong>PHI Warning:</strong> This deletion will soft-delete {impact.encountersCount} clinical encounters
            containing Protected Health Information (PHI). Data will be marked as deleted but retained for compliance purposes.
          </p>
        </div>
      )}
    </div>
  );
};
